import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import {
  MessageSquare, Hash, User, Users, UserPlus, RefreshCw, Loader2, AlertCircle,
  Send, ChevronDown, Search, X, Paperclip, ExternalLink, Plus, MoreHorizontal,
  LogOut,
} from 'lucide-react';
import { ChatContext } from '../../contexts/ChatContext';
import GoogleConnectRequired from '../GoogleConnectRequired';

const BRAND = '#00B2FF';

// 모듈 레벨 캐시 — 페이지 전환 후 재방문 시에도 유지
const _spacesCache = { data: null };        // fetchSpaces 결과
const _mediaBlobCache = new Map();          // proxyUrl → blobUrl

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (now - d < 86400000 && d.getDate() === now.getDate())
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  if (now - d < 7 * 86400000)
    return d.toLocaleDateString('ko-KR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function isSameGroup(prev, cur) {
  if (!prev) return false;
  return prev.sender === cur.sender && prev.isMe === cur.isMe &&
    (new Date(cur.createTime) - new Date(prev.createTime)) < 5 * 60 * 1000;
}

function highlight(text, q) {
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>{text.slice(0, idx)}<mark className="bg-yellow-400/40 text-inherit rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>
  );
}

const URL_RE = /https?:\/\/[^\s<>"']+[^\s<>"'.!?,;:)]/g;

function renderText(text, searchQ) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) {
      const chunk = text.slice(last, m.index);
      parts.push(searchQ ? highlight(chunk, searchQ) : chunk);
    }
    parts.push(
      <a key={m.index} href={m[0]} target="_blank" rel="noreferrer noopener"
        className="underline underline-offset-2 opacity-90 hover:opacity-100 break-all"
        onClick={e => e.stopPropagation()}>
        {m[0]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    const chunk = text.slice(last);
    parts.push(searchQ ? highlight(chunk, searchQ) : chunk);
  }
  return parts.length ? parts : text;
}

function authHeaders() {
  const token = localStorage.getItem('mfg_token');
  return { Authorization: `Bearer ${token}` };
}

function Avatar({ name, url, size = 8 }) {
  const [err, setErr] = useState(false);
  const initials = (name || '?').split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
  if (url && !err) {
    return <img src={url} alt={name || '?'} referrerPolicy="no-referrer"
      className={`w-${size} h-${size} rounded-full flex-shrink-0 object-cover`}
      onError={() => setErr(true)} />;
  }
  return (
    <div className={`w-${size} h-${size} rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold`}
      style={{ background: `linear-gradient(135deg, ${BRAND}, #0090D4)`, color: '#fff' }}>
      {initials}
    </div>
  );
}

function SpaceIcon({ type, active, avatarUrl, name }) {
  const [err, setErr] = useState(false);
  if (avatarUrl && !err) {
    return (
      <img src={avatarUrl} alt={name || ''} referrerPolicy="no-referrer"
        className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
        style={{ outline: active ? `2px solid ${BRAND}` : 'none' }}
        onError={() => setErr(true)} />
    );
  }
  const Icon = type === 'DIRECT_MESSAGE' ? User : Hash;
  return (
    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: active ? BRAND : 'rgba(255,255,255,0.07)', color: active ? '#fff' : '#6b7280' }}>
      <Icon size={13} />
    </div>
  );
}

function ChatAuthImage({ proxyUrl, contentName, onOpen, onLoad }) {
  const [blobUrl, setBlobUrl] = useState(() => _mediaBlobCache.get(proxyUrl) || null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (_mediaBlobCache.get(proxyUrl)) return; // 캐시 히트 → 재fetch 없음
    let cancelled = false;
    fetch(proxyUrl, { headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        _mediaBlobCache.set(proxyUrl, url);
        if (!cancelled) setBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [proxyUrl]);
  if (err) return <div className="text-xs text-gray-500 px-2 py-1 bg-gray-800 rounded">{contentName || '이미지 로드 실패'}</div>;
  if (!blobUrl) return <div className="w-[120px] h-[80px] bg-gray-800 rounded-lg animate-pulse" />;
  return (
    <img src={blobUrl} alt={contentName || '이미지'}
      className="max-w-[260px] rounded-lg cursor-pointer hover:opacity-90 border border-gray-700"
      onClick={onOpen} onLoad={onLoad} />
  );
}

function Attachments({ attachments, onImageLoad }) {
  if (!attachments?.length) return null;
  return (
    <div className="flex flex-col gap-1 mt-1">
      {attachments.map((att, i) => {
        if (att.contentType?.startsWith('image/')) {
          const driveHref = att.driveFileId ? `https://drive.google.com/file/d/${att.driveFileId}` : null;
          if (att.s3Url) {
            return <img key={i} src={att.s3Url} alt={att.contentName || '이미지'}
              className="max-w-[260px] rounded-lg cursor-pointer hover:opacity-90 border border-gray-700"
              onClick={() => window.open(att.s3Url, '_blank')} onLoad={onImageLoad} />;
          }
          if (att.mediaResourceName) {
            const proxyUrl = `/api/google/chat/media?ref=${encodeURIComponent(att.mediaResourceName)}`;
            return <ChatAuthImage key={i} proxyUrl={proxyUrl} contentName={att.contentName}
              onOpen={() => window.open(driveHref || proxyUrl, '_blank')} onLoad={onImageLoad} />;
          }
          if (att.thumbnailUri) {
            return <img key={i} src={att.thumbnailUri} alt={att.contentName || '이미지'}
              className="max-w-[260px] rounded-lg cursor-pointer hover:opacity-90 border border-gray-700"
              onClick={() => window.open(driveHref || att.thumbnailUri, '_blank')} />;
          }
          return null;
        }
        const proxyUrl = att.mediaResourceName
          ? `/api/google/chat/media?ref=${encodeURIComponent(att.mediaResourceName)}&name=${encodeURIComponent(att.contentName || 'file')}`
          : null;
        const href = att.s3Url || proxyUrl || (att.driveFileId ? `https://drive.google.com/file/d/${att.driveFileId}` : null);
        if (!href) return null;
        const isProxy = !!proxyUrl && !att.s3Url;
        return (
          <a key={i} href={href} target="_blank" rel="noreferrer" {...(isProxy ? { download: att.contentName || 'file' } : {})}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-700/80 rounded-lg text-xs text-blue-300 hover:text-blue-200 max-w-[260px]">
            <Paperclip size={11} className="flex-shrink-0" />
            <span className="truncate">{att.contentName || '첨부파일'}</span>
            <ExternalLink size={10} className="flex-shrink-0 ml-auto" />
          </a>
        );
      })}
    </div>
  );
}

function SpaceItem({ space, onClick, active, unread, isMenuOpen, onMenuToggle, onMenuClose, onLeave }) {
  return (
    <div className="group relative">
      <div role="button" tabIndex={0} onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick()}
        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors cursor-pointer ${
          active ? 'text-brand' : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
        }`}
        style={active ? { background: 'linear-gradient(90deg, rgba(0,178,255,0.18), transparent)' } : {}}>
        <SpaceIcon type={space.type} active={active} avatarUrl={space.avatarUrl} name={space.displayName} />
        <span className="flex-1 truncate font-medium">{space.displayName}</span>
        {unread > 0 && (
          <span className="flex-shrink-0 min-w-[1.25rem] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1"
            style={{ background: BRAND, color: '#fff' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        {unread === 0 && (
          <button onClick={e => { e.stopPropagation(); onMenuToggle(); }}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded text-gray-500 hover:text-white transition-all">
            <MoreHorizontal size={12} />
          </button>
        )}
      </div>
      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={onMenuClose} />
          <div className="absolute right-2 top-7 z-30 w-36 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
            <button onClick={onLeave}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors">
              <LogOut size={12} />
              나가기 / 삭제
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function normalizeDbMsg(row, myGoogleId) {
  let attachments = [];
  try { attachments = row.attachments ? JSON.parse(row.attachments) : []; } catch (_) {}
  return {
    name: row.messageName,
    text: row.text || '',
    sender: row.senderDisplayName || '알 수 없음',
    senderName: row.senderName || '',
    isMe: myGoogleId ? row.senderName === `users/${myGoogleId}` : false,
    avatarUrl: row.senderName ? `/api/google/chat/avatar/${row.senderName.replace('users/', '')}` : null,
    createTime: row.createTime,
    attachments,
  };
}

export default function GoogleChatPanel() {
  const { socketRef, socket, gchatUnreadSpaces, setGchatUnreadSpaces, gchatActiveSpaceRef, gchatSpaceNamesRef } = useContext(ChatContext) || {};

  const [spaces, setSpaces] = useState([]);
  const [myAvatarUrl, setMyAvatarUrl] = useState(null);
  const [myGoogleId, setMyGoogleId] = useState(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [activeSpace, setActiveSpace] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [spaceSearch, setSpaceSearch] = useState('');
  const [msgSearch, setMsgSearch] = useState('');
  const [showMsgSearch, setShowMsgSearch] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [readStates, setReadStates] = useState({});
  const readStatesRef = useRef({});
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addMemberError, setAddMemberError] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [showCreateSpace, setShowCreateSpace] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [chatScopeError, setChatScopeError] = useState(false);
  const [creatingSpace, setCreatingSpace] = useState(false);
  const [createSpaceError, setCreateSpaceError] = useState('');
  const [spaceMenuId, setSpaceMenuId] = useState(null);

  const scrollRef = useRef(null);
  const wasAtBottomRef = useRef(true);
  const lastMsgTimeRef = useRef(null);
  const oldestTimeRef = useRef(null);
  const hasMoreRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const watchedSpaceRef = useRef(null);
  const activeSpaceRef = useRef(null);
  const msgSearchRef = useRef(null);
  const spacesRef = useRef([]);
  const myGoogleIdRef = useRef(null);
  const msgsCacheRef = useRef({});

  useEffect(() => { activeSpaceRef.current = activeSpace; }, [activeSpace]);
  useEffect(() => { spacesRef.current = spaces; }, [spaces]);
  useEffect(() => { myGoogleIdRef.current = myGoogleId; }, [myGoogleId]);
  useEffect(() => { readStatesRef.current = readStates; }, [readStates]);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const total = Object.values(gchatUnreadSpaces || {}).reduce((a, b) => a + b, 0);
    localStorage.setItem('gchat_unread', String(total));
    window.dispatchEvent(new CustomEvent('gchat-unread', { detail: { count: total } }));
  }, [gchatUnreadSpaces]);

  useEffect(() => {
    if (!socket) return;

    const handler = ({ spaceId, messages: incoming }) => {
      const enriched = incoming.map(m => ({
        ...m,
        isMe: myGoogleIdRef.current ? m.senderName === `users/${myGoogleIdRef.current}` : (m.isMe || false),
        avatarUrl: m.senderName ? `/api/google/chat/avatar/${m.senderName.replace('users/', '')}` : (m.avatarUrl || null),
      }));

      if (spaceId === activeSpaceRef.current?.spaceId) {
        setMessages(prev => {
          const seen = new Set(prev.map(m => m.name));
          const fresh = enriched.filter(m => !seen.has(m.name));
          if (!fresh.length) return prev;
          lastMsgTimeRef.current = fresh[fresh.length - 1].createTime;
          const el = scrollRef.current;
          const near = !el || wasAtBottomRef.current;
          if (near) setTimeout(() => {
            if (scrollRef.current) { scrollRef.current.scrollTop = scrollRef.current.scrollHeight; wasAtBottomRef.current = true; }
          }, 60);
          const updated = [...prev, ...fresh];
          msgsCacheRef.current[spaceId] = updated;
          return updated;
        });
      }
      // 비활성 스페이스의 unread 뱃지 + OS 알림은 ChatContext의 전역 리스너가 처리
    };

    const attHandler = ({ spaceId, messageName, attachments }) => {
      if (spaceId === activeSpaceRef.current?.spaceId) {
        setMessages(prev => prev.map(m => m.name === messageName ? { ...m, attachments } : m));
      }
    };

    const readHandler = ({ spaceId, googleId, lastReadTime }) => {
      if (spaceId === activeSpaceRef.current?.spaceId) {
        setReadStates(prev => ({ ...prev, [googleId]: lastReadTime }));
      }
    };

    const authRequiredHandler = () => setChatScopeError(true);
    const reconnectedHandler = () => {
      setChatScopeError(false);
      if (activeSpaceRef.current) loadMessages(activeSpaceRef.current);
    };

    socket.on('google_chat_messages', handler);
    socket.on('google_chat_attachment_updated', attHandler);
    socket.on('google_chat_read_update', readHandler);
    socket.on('google_chat_auth_required', authRequiredHandler);
    socket.on('google_chat_reconnected', reconnectedHandler);
    return () => {
      socket.off('google_chat_messages', handler);
      socket.off('google_chat_attachment_updated', attHandler);
      socket.off('google_chat_read_update', readHandler);
      socket.off('google_chat_auth_required', authRequiredHandler);
      socket.off('google_chat_reconnected', reconnectedHandler);
    };
  }, [socket]);

  // 소켓 연결 후 모든 스페이스 구독 (push 수신용)
  useEffect(() => {
    if (!socket || !spaces.length) return;
    spaces.slice(0, 20).forEach(space => {
      socketRef?.current?.emit('watch_google_space', {
        spaceId: space.spaceId,
        spaceDisplayName: space.displayName,
      });
    });
  }, [socket, spaces.length]); // eslint-disable-line

  const fetchSpaces = useCallback(async (force = false) => {
    // 캐시 히트: 즉시 표시 후 백그라운드에서 최신화
    if (_spacesCache.data && !force) {
      const d = _spacesCache.data;
      setSpaces(d.spaces);
      if (d.myAvatarUrl) setMyAvatarUrl(d.myAvatarUrl);
      if (d.myId) {
        setMyGoogleId(d.myId); myGoogleIdRef.current = d.myId;
        localStorage.setItem('gchat_my_id', d.myId);
      }
      if (gchatSpaceNamesRef && Array.isArray(d.spaces)) d.spaces.forEach(s => { gchatSpaceNamesRef.current[s.spaceId] = s.displayName; });
      setNeedsReauth(!!d.needsReauth);
      setLoading(false);
      return;
    }
    setLoading(true); setError('');
    try {
      const r = await fetch(`/api/google/chat/spaces${force ? '?force=1' : ''}`, { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 서버가 재연동이 필요하다고 명시한 경우에만 연결 화면을 띄운다.
        // 쿼터 초과·일시 오류까지 연결 화면으로 덮으면 재연동해도 해결되지 않는다.
        if (d.code === 'GOOGLE_NOT_CONNECTED' || d.code === 'GOOGLE_REAUTH_REQUIRED') {
          setNeedsAuth(true);
          return;
        }
        setNeedsAuth(false);
        setError(d.message || `오류 ${r.status}`);
        return;
      }
      setNeedsAuth(false);
      const spaceList = Array.isArray(d.spaces) ? d.spaces : [];
      _spacesCache.data = d;
      setSpaces(spaceList);
      if (d.myAvatarUrl) setMyAvatarUrl(d.myAvatarUrl);
      if (d.myId) {
        setMyGoogleId(d.myId); myGoogleIdRef.current = d.myId;
        localStorage.setItem('gchat_my_id', d.myId);
      }
      // ChatContext의 gchatSpaceNamesRef 업데이트 (알림용 표시명)
      if (gchatSpaceNamesRef) spaceList.forEach(s => { gchatSpaceNamesRef.current[s.spaceId] = s.displayName; });
      setNeedsReauth(!!d.needsReauth);
    } catch (e) { setError('오류: ' + e.message); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  const fetchDbMessages = useCallback(async (spaceId, before = null, limit = 50) => {
    try {
      const params = [`limit=${limit}`];
      if (before) params.push(`before=${encodeURIComponent(before)}`);
      const r = await fetch(`/api/google/chat/db/spaces/${spaceId}/messages?${params.join('&')}`, { headers: authHeaders() });
      if (!r.ok) return [];
      const rows = await r.json();
      return rows.map(row => normalizeDbMsg(row, myGoogleIdRef.current));
    } catch { return []; }
  }, []);

  const scrollToBottom = () => {
    if (scrollRef.current) { scrollRef.current.scrollTop = scrollRef.current.scrollHeight; wasAtBottomRef.current = true; }
  };

  const loadMessages = useCallback(async (space) => {
    if (!space) return;

    // 캐시 히트: 즉시 표시 (로딩 없음)
    const cached = msgsCacheRef.current[space.spaceId];
    if (cached) {
      setMessages(cached);
      setMsgLoading(false);
    } else {
      setMsgLoading(true);
      setMessages([]);
    }

    setReadStates({});
    setHasMore(false); hasMoreRef.current = false;
    setLoadingOlder(false); loadingOlderRef.current = false;
    lastMsgTimeRef.current = null; oldestTimeRef.current = null;
    wasAtBottomRef.current = true;
    setMsgSearch(''); setShowMsgSearch(false);
    setShowMembersPanel(false);

    const dbMsgs = await fetchDbMessages(space.spaceId);
    const list = Array.isArray(dbMsgs) ? dbMsgs : [];
    msgsCacheRef.current[space.spaceId] = list;

    if (activeSpaceRef.current?.spaceId === space.spaceId) {
      setMessages(list);
      if (list.length > 0) {
        oldestTimeRef.current = list[0].createTime;
        lastMsgTimeRef.current = list[list.length - 1].createTime;
        if (list.length >= 50) { hasMoreRef.current = true; setHasMore(true); }
      }
      setMsgLoading(false);
      setTimeout(scrollToBottom, 80);
    }

    watchedSpaceRef.current = space.spaceId;
    socketRef?.current?.emit('watch_google_space', {
      spaceId: space.spaceId,
      spaceDisplayName: space.displayName,
      lastTime: lastMsgTimeRef.current,
    });

    fetch(`/api/google/chat/spaces/${space.spaceId}/read-states`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(states => {
        const map = {};
        states.forEach(s => { map[s.googleId] = s.lastReadTime; });
        setReadStates(map);
      }).catch(() => {});

    if (setGchatUnreadSpaces) setGchatUnreadSpaces(prev => { const n = { ...prev }; delete n[space.spaceId]; return n; });
  }, [fetchDbMessages, socketRef, setGchatUnreadSpaces]);

  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreRef.current || !oldestTimeRef.current || !activeSpaceRef.current) return;
    loadingOlderRef.current = true; setLoadingOlder(true);
    const el = scrollRef.current;
    const prevScrollBottom = el ? el.scrollHeight - el.scrollTop : 0;
    const msgs = await fetchDbMessages(activeSpaceRef.current.spaceId, oldestTimeRef.current);
    if (!msgs.length) { hasMoreRef.current = false; setHasMore(false); }
    else {
      if (msgs.length < 50) { hasMoreRef.current = false; setHasMore(false); }
      oldestTimeRef.current = msgs[0].createTime;
      setMessages(prev => {
        const seen = new Set(prev.map(m => m.name));
        return [...msgs.filter(m => !seen.has(m.name)), ...prev];
      });
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevScrollBottom; });
    }
    loadingOlderRef.current = false; setLoadingOlder(false);
  }, [fetchDbMessages]);

  const runGlobalSearch = useCallback(async (q) => {
    if (!q.trim()) { setGlobalResults([]); return; }
    setGlobalLoading(true);
    try {
      const r = await fetch(`/api/google/chat/db/search?q=${encodeURIComponent(q)}`, { headers: authHeaders() });
      const rows = r.ok ? await r.json() : [];
      const results = rows.map(row => ({
        ...normalizeDbMsg(row, myGoogleIdRef.current),
        spaceId: row.spaceName?.replace('spaces/', ''),
        spaceName: row.spaceDisplayName || row.spaceName,
      }));
      setGlobalResults(results);
    } catch { setGlobalResults([]); }
    setGlobalLoading(false);
  }, []);

  useEffect(() => { fetchSpaces(); }, [fetchSpaces]);
  useEffect(() => { if (!activeSpace) return; loadMessages(activeSpace); }, [activeSpace?.spaceId]); // eslint-disable-line

  // 언마운트 시 gchatActiveSpaceRef 초기화 (ChatContext 리스너가 알림 처리하도록)
  useEffect(() => {
    return () => { if (gchatActiveSpaceRef) gchatActiveSpaceRef.current = null; };
  }, [gchatActiveSpaceRef]);

  const selectSpace = useCallback((space) => {
    setActiveSpace(space);
    setSpaceMenuId(null);
    if (gchatActiveSpaceRef) gchatActiveSpaceRef.current = space?.spaceId || null;
    if (space && setGchatUnreadSpaces) {
      setGchatUnreadSpaces(prev => { const n = { ...prev }; delete n[space.spaceId]; return n; });
    }
  }, [gchatActiveSpaceRef, setGchatUnreadSpaces]);

  const addFiles = (files) => {
    setPendingFiles(prev => [...prev, ...Array.from(files)]);
  };

  async function sendMessage() {
    const text = input.trim();
    if (!text && !pendingFiles.length || !activeSpace || sending) return;
    setInput(''); setSending(true);
    const filesToSend = [...pendingFiles];
    setPendingFiles([]);
    try {
      if (filesToSend.length > 0) {
        for (let i = 0; i < filesToSend.length; i++) {
          const file = filesToSend[i];
          const fd = new FormData();
          fd.append('file', file);
          if (i === filesToSend.length - 1 && text) fd.append('text', text);
          const r = await fetch(`/api/google/chat/spaces/${activeSpace.spaceId}/attach`, {
            method: 'POST', headers: authHeaders(), body: fd,
          });
          if (r.ok) {
            const msg = await r.json();
            msg.avatarUrl = msg.avatarUrl || myAvatarUrl;
            setMessages(prev => [...prev, msg]);
            lastMsgTimeRef.current = msg.createTime;
            setTimeout(scrollToBottom, 60);
          }
        }
      } else if (text) {
        const r = await fetch(`/api/google/chat/spaces/${activeSpace.spaceId}/messages`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        if (r.ok) {
          const msg = await r.json();
          msg.avatarUrl = msg.avatarUrl || myAvatarUrl;
          setMessages(prev => [...prev, msg]);
          lastMsgTimeRef.current = msg.createTime;
          setTimeout(scrollToBottom, 60);
        } else {
          const err = await r.json().catch(() => ({}));
          setError(`전송 실패: ${err.message || '알 수 없는 오류'}`);
        }
      }
    } finally { setSending(false); }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); sendMessage(); }
  }

  const createSpace = async () => {
    if (!newSpaceName.trim() || creatingSpace) return;
    setCreatingSpace(true); setCreateSpaceError('');
    try {
      const r = await fetch('/api/google/chat/spaces', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: newSpaceName.trim() }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setCreateSpaceError(d.message || '생성 실패'); return; }
      const space = await r.json();
      setSpaces(prev => [space, ...prev]);
      setShowCreateSpace(false); setNewSpaceName('');
      socketRef?.current?.emit('watch_google_space', { spaceId: space.spaceId, spaceDisplayName: space.displayName });
      selectSpace(space);
    } finally { setCreatingSpace(false); }
  };

  const leaveOrDeleteSpace = async (space) => {
    if (!window.confirm(`'${space.displayName}'에서 나가시겠습니까?`)) return;
    setSpaceMenuId(null);
    try {
      const r = await fetch(`/api/google/chat/spaces/${space.spaceId}`, { method: 'DELETE', headers: authHeaders() });
      if (r.ok) {
        setSpaces(prev => prev.filter(s => s.spaceId !== space.spaceId));
        if (setGchatUnreadSpaces) setGchatUnreadSpaces(prev => { const n = { ...prev }; delete n[space.spaceId]; return n; });
        socketRef?.current?.emit('unwatch_google_space', { spaceId: space.spaceId });
        if (activeSpace?.spaceId === space.spaceId) {
          setActiveSpace(null); activeSpaceRef.current = null;
          watchedSpaceRef.current = null;
        }
      } else {
        const d = await r.json().catch(() => ({}));
        alert(d.message || '작업 실패');
      }
    } catch (e) { alert('오류: ' + e.message); }
  };

  const fetchMembers = useCallback(async (spaceId) => {
    setMembersLoading(true); setMembers([]);
    try {
      const r = await fetch(`/api/google/chat/spaces/${spaceId}/members`, { headers: authHeaders() });
      if (r.ok) setMembers(await r.json());
    } finally { setMembersLoading(false); }
  }, []);

  const toggleMembersPanel = () => {
    setShowMembersPanel(v => {
      if (!v && activeSpace) fetchMembers(activeSpace.spaceId);
      return !v;
    });
  };

  const addMember = async () => {
    if (!addMemberEmail.trim() || addingMember || !activeSpace) return;
    setAddingMember(true); setAddMemberError('');
    try {
      const r = await fetch(`/api/google/chat/spaces/${activeSpace.spaceId}/members`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addMemberEmail.trim() }),
      });
      if (r.ok) {
        const newMember = await r.json();
        setMembers(prev => [...prev, newMember]);
        setAddMemberEmail('');
      } else {
        const d = await r.json().catch(() => ({}));
        setAddMemberError(d.message || '추가 실패');
      }
    } finally { setAddingMember(false); }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`'${member.displayName}'를 스페이스에서 제거하시겠습니까?`)) return;
    try {
      const memberId = member.name.split('/').pop();
      const r = await fetch(`/api/google/chat/spaces/${activeSpace.spaceId}/members/${memberId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (r.ok) setMembers(prev => prev.filter(m => m.name !== member.name));
      else { const d = await r.json().catch(() => ({})); alert(d.message || '제거 실패'); }
    } catch (e) { alert('오류: ' + e.message); }
  };

  const filteredMessages = msgSearch
    ? messages.filter(m => m.text?.toLowerCase().includes(msgSearch.toLowerCase()))
    : messages;
  const filteredSpaces = spaceSearch
    ? spaces.filter(s => s.displayName.toLowerCase().includes(spaceSearch.toLowerCase()))
    : spaces;

  if (needsAuth || needsReauth) return <GoogleConnectRequired message="Google Chat을 사용하려면 Google 계정을 연결해야 합니다." />;

  return (
    <div className="flex w-full h-full overflow-hidden bg-gray-900">

      {/* ── 스페이스 사이드바 ── */}
      <div className="w-56 flex-shrink-0 flex flex-col bg-gray-950 border-r border-gray-800">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-800 flex-shrink-0">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: BRAND, color: '#fff' }}>
            <MessageSquare size={11} />
          </div>
          <span className="text-sm font-semibold text-white flex-1">Google Chat</span>
          <button onClick={() => { setShowGlobalSearch(v => !v); setGlobalSearch(''); setGlobalResults([]); }}
            className={`transition-colors ${showGlobalSearch ? 'text-brand' : 'text-gray-500 hover:text-white'}`} title="전체 검색">
            <Search size={13} />
          </button>
          <button onClick={() => { setShowCreateSpace(true); setCreateSpaceError(''); setNewSpaceName(''); }}
            className="text-gray-500 hover:text-white transition-colors" title="스페이스 만들기">
            <Plus size={13} />
          </button>
          <button onClick={() => fetchSpaces(true)} disabled={loading} className="text-gray-500 hover:text-white transition-colors">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>


        {showGlobalSearch && (
          <div className="px-3 py-2 border-b border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-gray-800 rounded-lg px-2 py-1.5">
              <Search size={11} className="text-gray-500 flex-shrink-0" />
              <input autoFocus value={globalSearch} onChange={e => setGlobalSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runGlobalSearch(globalSearch)}
                placeholder="전체 검색... (Enter)"
                className="flex-1 bg-transparent text-xs text-white placeholder-gray-500 outline-none" />
              {globalSearch && (
                <button onClick={() => { setGlobalSearch(''); setGlobalResults([]); }} className="text-gray-500 hover:text-white">
                  <X size={11} />
                </button>
              )}
            </div>
          </div>
        )}

        {showGlobalSearch && (globalLoading || globalResults.length > 0) && (
          <div className="flex-1 overflow-y-auto">
            {globalLoading && <div className="flex justify-center py-4"><Loader2 size={14} className="animate-spin text-gray-500" /></div>}
            {!globalLoading && globalResults.map((r, i) => (
              <button key={i} onClick={() => {
                const sp = spaces.find(s => s.spaceId === r.spaceId);
                if (sp) { selectSpace(sp); setShowGlobalSearch(false); }
              }}
                className="w-full text-left px-3 py-2 hover:bg-gray-800/60 border-b border-gray-800/50">
                <div className="text-[10px] text-gray-500 mb-0.5 truncate">{r.spaceName}</div>
                <div className="text-[11px] text-white leading-tight line-clamp-2">{highlight(r.text, globalSearch)}</div>
                <div className="text-[9px] text-gray-600 mt-0.5">{fmtTime(r.createTime)}</div>
              </button>
            ))}
          </div>
        )}

        {(!showGlobalSearch || (!globalLoading && !globalResults.length)) && (
          <>
            <div className="px-3 py-2 border-b border-gray-800/50 flex-shrink-0">
              <div className="flex items-center gap-1.5 bg-gray-800/60 rounded px-2 py-1">
                <Search size={10} className="text-gray-600 flex-shrink-0" />
                <input value={spaceSearch} onChange={e => setSpaceSearch(e.target.value)}
                  placeholder="스페이스 찾기..."
                  className="flex-1 bg-transparent text-[11px] text-white placeholder-gray-600 outline-none" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {loading && <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>}
              {!loading && !error && filteredSpaces.length === 0 && (
                <p className="px-3 py-6 text-xs text-gray-500 text-center">스페이스가 없습니다</p>
              )}
              {!loading && filteredSpaces.map(space => (
                <SpaceItem key={space.name} space={space}
                  onClick={() => selectSpace(space)}
                  active={activeSpace?.name === space.name}
                  unread={(gchatUnreadSpaces || {})[space.spaceId] || 0}
                  isMenuOpen={spaceMenuId === space.spaceId}
                  onMenuToggle={() => setSpaceMenuId(spaceMenuId === space.spaceId ? null : space.spaceId)}
                  onMenuClose={() => setSpaceMenuId(null)}
                  onLeave={() => leaveOrDeleteSpace(space)} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── 메시지 영역 ── */}
      {activeSpace ? (
        <div className="flex-1 flex min-w-0 relative overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-800 flex-shrink-0">
              <SpaceIcon type={activeSpace.type} active avatarUrl={activeSpace.avatarUrl} name={activeSpace.displayName} />
              <span className="text-sm font-semibold text-white truncate flex-1">{activeSpace.displayName}</span>
              {msgLoading && <Loader2 size={13} className="animate-spin text-gray-500" />}
              <button onClick={() => { setShowMsgSearch(v => !v); setMsgSearch(''); setTimeout(() => msgSearchRef.current?.focus(), 50); }}
                className={`transition-colors ${showMsgSearch ? 'text-brand' : 'text-gray-500 hover:text-white'}`}>
                <Search size={14} />
              </button>
              <button onClick={toggleMembersPanel}
                className={`transition-colors ${showMembersPanel ? 'text-brand' : 'text-gray-500 hover:text-white'}`}>
                <Users size={15} />
              </button>
              <button
                onClick={() => window.open(`/chat-popup/${activeSpace.spaceId}`, 'gchat_popup', 'popup=yes,width=900,height=700,resizable=yes')}
                className="text-gray-500 hover:text-white transition-colors"
                title="새 창으로 열기">
                <ExternalLink size={14} />
              </button>
            </div>

            {showMsgSearch && (
              <div className="px-4 py-2 border-b border-gray-800 flex-shrink-0 flex items-center gap-2">
                <Search size={13} className="text-gray-500 flex-shrink-0" />
                <input ref={msgSearchRef} value={msgSearch} onChange={e => setMsgSearch(e.target.value)}
                  placeholder="이 스페이스에서 검색..."
                  className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 outline-none" />
                {msgSearch && <span className="text-[11px] text-gray-500">{filteredMessages.length}건</span>}
                <button onClick={() => { setShowMsgSearch(false); setMsgSearch(''); }} className="text-gray-500 hover:text-white">
                  <X size={14} />
                </button>
              </div>
            )}

            {chatScopeError && (
              <div className="mx-4 mt-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2 flex-shrink-0">
                <AlertCircle size={13} className="text-yellow-400 flex-shrink-0" />
                <span className="text-xs text-yellow-200 flex-1">Google Chat 권한이 없어 새 메시지를 받을 수 없습니다. Google 계정을 재연결해 주세요.</span>
                <button
                  onClick={() => { window.location.href = '/google/connect'; }}
                  className="flex-shrink-0 px-2 py-0.5 bg-yellow-500/20 hover:bg-yellow-500/40 text-yellow-300 text-xs rounded font-semibold transition-colors"
                >재연결</button>
                <button onClick={() => setChatScopeError(false)} className="text-gray-500 hover:text-white flex-shrink-0"><X size={12} /></button>
              </div>
            )}

            {error && (
              <div className="mx-4 mt-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 flex-shrink-0">
                <AlertCircle size={13} className="text-red-400 flex-shrink-0" />
                <span className="text-xs text-red-300 flex-1 truncate">{error}</span>
                <button onClick={() => setError('')} className="text-gray-500 hover:text-white flex-shrink-0"><X size={12} /></button>
              </div>
            )}

            <div ref={scrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 space-y-0.5"
              onScroll={e => {
                const el = e.target;
                const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                wasAtBottomRef.current = distFromBottom < 80;
                setShowScrollBtn(distFromBottom > 200);
                if (el.scrollTop < 100 && !msgSearch) loadOlder();
              }}>
              {!msgSearch && loadingOlder && <div className="flex justify-center py-3"><div className="w-4 h-4 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>}
              {!msgSearch && !loadingOlder && hasMore && <div className="text-center text-[10px] text-gray-600 py-2">↑ 위로 스크롤하면 이전 메시지를 불러옵니다</div>}
              {msgLoading && <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>}
              {!msgLoading && filteredMessages.length === 0 && (
                <div className="flex items-center justify-center h-24 text-sm text-gray-600">
                  {msgSearch ? '검색 결과가 없습니다.' : '메시지가 없습니다.'}
                </div>
              )}
              {!msgLoading && (() => {
                const lastIsMeIdx = filteredMessages.reduce((acc, m, i) => m.isMe ? i : acc, -1);
                return filteredMessages.map((msg, idx) => {
                  const grouped = !msgSearch && isSameGroup(filteredMessages[idx - 1] || null, msg);
                  let readReceipt = null;
                  if (msg.isMe && idx === lastIsMeIdx && msg.createTime) {
                    const msgTime = new Date(msg.createTime).getTime();
                    const readersCount = Object.entries(readStates)
                      .filter(([gid]) => gid !== myGoogleId)
                      .filter(([, t]) => new Date(t).getTime() >= msgTime)
                      .length;
                    readReceipt = (
                      <span className={`text-[9px] mt-0.5 mr-0.5 ${readersCount > 0 ? 'text-brand/70' : 'text-gray-600'}`}>
                        {readersCount > 0 ? (readersCount === 1 ? '읽음' : `${readersCount}명 읽음`) : '안읽음'}
                      </span>
                    );
                  }
                  return (
                    <div key={msg.name || idx} className={`flex gap-2.5 ${grouped ? '' : 'pt-2'} ${msg.isMe ? 'flex-row-reverse' : ''}`}>
                      <div className="w-8 flex-shrink-0">
                        {!grouped && (msg.isMe
                          ? <Avatar name={msg.sender || '나'} url={myAvatarUrl || msg.avatarUrl} />
                          : <Avatar name={msg.sender} url={msg.avatarUrl} />
                        )}
                      </div>
                      <div className={`min-w-0 max-w-[72%] flex flex-col ${msg.isMe ? 'items-end' : 'items-start'}`}>
                        {!grouped && (
                          <div className={`flex items-baseline gap-2 mb-1 ${msg.isMe ? 'flex-row-reverse' : ''}`}>
                            {!msg.isMe && <span className="text-xs font-semibold text-white">{msg.sender}</span>}
                            <span className="text-[10px] text-gray-500">{fmtTime(msg.createTime)}</span>
                          </div>
                        )}
                        {msg.text && (
                          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed break-words whitespace-pre-wrap ${
                            msg.isMe ? 'bg-brand text-white rounded-tr-sm' : 'bg-gray-800 text-gray-200 rounded-tl-sm'
                          }`}>
                            {renderText(msg.text, msgSearch || null)}
                          </div>
                        )}
                        <Attachments attachments={msg.attachments} onImageLoad={() => {
                          if (wasAtBottomRef.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                        }} />
                        {readReceipt}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {showScrollBtn && !msgSearch && (
              <button onClick={scrollToBottom}
                className="absolute bottom-20 right-5 w-8 h-8 rounded-full text-white flex items-center justify-center z-10"
                style={{ background: BRAND }}>
                <ChevronDown size={16} />
              </button>
            )}

            <div
              className={`border-t px-4 py-3 flex-shrink-0 transition-colors ${dragOver ? 'border-brand/50 bg-brand/5' : 'border-gray-800'}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
              onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}>
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {pendingFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 px-2 py-1 bg-gray-700/80 rounded-full text-xs text-gray-300 max-w-[180px]">
                      <Paperclip size={10} className="flex-shrink-0 text-gray-400" />
                      <span className="truncate">{f.name}</span>
                      <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))} className="flex-shrink-0 text-gray-500 hover:text-gray-200 ml-0.5">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input type="file" multiple ref={fileInputRef} className="hidden"
                  onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
                <button onClick={() => fileInputRef.current?.click()}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-300 hover:bg-gray-700/50 transition-colors">
                  <Paperclip size={15} />
                </button>
                <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={dragOver ? '파일을 여기에 놓으세요...' : `${activeSpace.displayName}에 메시지 보내기...`} rows={1}
                  className="flex-1 resize-none rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand leading-5 min-h-[2.25rem] max-h-28 overflow-y-auto" />
                <button onClick={sendMessage} disabled={(!input.trim() && !pendingFiles.length) || sending}
                  className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-white disabled:opacity-40 transition-all"
                  style={{ background: BRAND }}>
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
              <p className="text-[10px] text-gray-600 mt-1.5 pl-1">Enter 전송 · Shift+Enter 줄바꿈 · 드래그 파일 첨부</p>
            </div>
          </div>

          {showMembersPanel && (
            <div className="w-60 flex-shrink-0 flex flex-col bg-gray-950 border-l border-gray-800">
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-800 flex-shrink-0">
                <Users size={13} className="text-gray-400" />
                <span className="text-sm font-semibold text-white flex-1">멤버</span>
                {membersLoading && <Loader2 size={11} className="animate-spin text-gray-500" />}
                <button onClick={() => fetchMembers(activeSpace.spaceId)} className="text-gray-500 hover:text-white">
                  <RefreshCw size={11} />
                </button>
                <button onClick={() => setShowMembersPanel(false)} className="text-gray-500 hover:text-white">
                  <X size={13} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2">
                {members.length === 0 && !membersLoading && (
                  <p className="text-[11px] text-gray-500 text-center py-4">멤버 정보가 없습니다</p>
                )}
                {members.map(m => (
                  <div key={m.name} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-800/50 group">
                    <Avatar name={m.displayName} url={m.avatarUrl} size={7} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate">{m.displayName}</div>
                      {m.role === 'ROLE_MANAGER' && <div className="text-[9px] text-brand">관리자</div>}
                      {m.isMe && <div className="text-[9px] text-gray-500">나</div>}
                    </div>
                    {!m.isMe && (
                      <button onClick={() => removeMember(m)}
                        className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition-all flex-shrink-0">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-800 px-3 py-3 flex-shrink-0">
                <div className="text-[10px] text-gray-500 mb-1.5 font-semibold uppercase tracking-wide">멤버 추가</div>
                <div className="flex gap-1.5">
                  <input value={addMemberEmail}
                    onChange={e => { setAddMemberEmail(e.target.value); setAddMemberError(''); }}
                    onKeyDown={e => e.key === 'Enter' && addMember()}
                    placeholder="이메일 입력..."
                    className="flex-1 min-w-0 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white placeholder-gray-600 outline-none focus:border-brand" />
                  <button onClick={addMember} disabled={!addMemberEmail.trim() || addingMember}
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded text-white disabled:opacity-40"
                    style={{ background: BRAND }}>
                    {addingMember ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
                  </button>
                </div>
                {addMemberError && <p className="text-[10px] text-red-400 mt-1">{addMemberError}</p>}
              </div>
            </div>
          )}
        </div>
      ) : error ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-12 h-12 mb-3 rounded-xl flex items-center justify-center bg-red-500/10">
            <AlertCircle size={24} className="text-red-400" />
          </div>
          <p className="text-sm text-red-400 font-medium mb-1">연결 오류</p>
          <p className="text-xs text-gray-500 text-center mb-4 max-w-xs leading-relaxed">{error}</p>
          <button onClick={fetchSpaces} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm text-white transition-all" style={{ background: BRAND }}>
            <RefreshCw size={13} />
            다시 시도
          </button>
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-600 p-6">
          <div className="w-12 h-12 mb-3 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,178,255,0.1)' }}>
            <MessageSquare size={24} style={{ color: BRAND }} />
          </div>
          <p className="text-sm text-gray-500 text-center">채널을 선택하면<br />대화를 시작할 수 있습니다</p>
          <button onClick={() => { setShowCreateSpace(true); setCreateSpaceError(''); setNewSpaceName(''); }}
            className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: BRAND, color: '#fff' }}>
            <Plus size={14} />
            새 스페이스 만들기
          </button>
        </div>
      )}

      {showCreateSpace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-80">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: BRAND, color: '#fff' }}>
                <Hash size={16} />
              </div>
              <h3 className="text-sm font-semibold text-white">새 스페이스 만들기</h3>
              <button onClick={() => setShowCreateSpace(false)} className="ml-auto text-gray-500 hover:text-white">
                <X size={15} />
              </button>
            </div>
            <input autoFocus value={newSpaceName}
              onChange={e => { setNewSpaceName(e.target.value); setCreateSpaceError(''); }}
              onKeyDown={e => e.key === 'Enter' && createSpace()}
              placeholder="스페이스 이름" maxLength={128}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 outline-none focus:border-brand mb-3" />
            {createSpaceError && <p className="text-xs text-red-400 mb-3">{createSpaceError}</p>}
            <div className="flex gap-2">
              <button onClick={() => setShowCreateSpace(false)}
                className="flex-1 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 transition-colors">
                취소
              </button>
              <button onClick={createSpace} disabled={!newSpaceName.trim() || creatingSpace}
                className="flex-1 px-4 py-2 rounded-lg text-sm text-white font-semibold disabled:opacity-40 transition-all"
                style={{ background: BRAND }}>
                {creatingSpace ? <span className="flex items-center justify-center gap-1.5"><Loader2 size={13} className="animate-spin" /> 생성 중...</span> : '만들기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
